/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    DELIVERY_INFORMATION_LEG_STATES,
    DELIVERY_INFORMATION_LEG_TYPES,
    DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS
} from '../configuration/delivery-information';
import {
    AnonymousDeliveryStepStatus,
    IAnonymousDeliveryEvent,
    IAnonymousDeliveryMainCarriage,
    IAnonymousDeliveryOrder,
    IAnonymousDeliveryStep
} from '../modules/lxs-anonymous-delivery/mocks/lxs-anonymous-delivery.mock-data';

export type OrderTrackingMessage = 'ORDER_TRACKING_SUCCESS' | 'ORDER_NOT_FOUND' | 'INVALID_VERIFICATION_CODE';

export interface IPostAnonymousDeliveryResponse {
    success: boolean;
    message?: string;
    order?: IAnonymousDeliveryOrder;
}

export interface IOrderTrackingEvent {
    Id: number;
    Name: string;
    Timestamp: string;
    Location?: string;
    ExtensionProperties: [];
}

export interface IOrderTrackingLegs {
    Id: number;
    LegType: string;
    ModeOfTransportation: string;
    State?: string;
    BlNumber: string;
    CarrierName: string;
    Container: string;
    ContainerType: string;
    ShipFlightNumber: string;
    Events?: IOrderTrackingEvent[];
    ExtensionProperties: [];
}

export interface IPostAnonymousDeliveryData {
    '@odata.context': string;
    CustomerPONumber?: string;
    EstimatedArrivalDate?: string;
    DeliveryNumber?: string;
    LiveTrackingAvailable?: boolean;
    LiveTrackingUrl?: string;
    Message: OrderTrackingMessage;
    Legs?: IOrderTrackingLegs[];
}

/**
 * PostAnonymousDelivery Input Action
 */
export class PostAnonymousDeliveryInput implements Msdyn365.IActionInput {
    public trackingId: string;
    public postalCode: string;
    constructor(trackingId: string, postalCode: string) {
        this.trackingId = trackingId;
        this.postalCode = postalCode;
    }
    public getCacheKey = () => `PostAnonymousDelivery-${this.trackingId}-${this.postalCode}`;
    public getCacheObjectType = () => 'PostAnonymousDelivery';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostAnonymousDeliveryInput('', '');
};

interface ISyntheticLeg {
    Id: number;
    ModeOfTransportation: string;
    Events: IOrderTrackingEvent[];
}

const createSyntheticLeg = (id: number, eventName: string, icon: string, timestamp?: string): ISyntheticLeg => ({
    Id: id,
    ModeOfTransportation: icon,
    Events: [{ Id: id, Name: eventName, Timestamp: timestamp ?? '', Location: '', ExtensionProperties: [] }]
});

const legIcon = (leg: { ModeOfTransportation?: string }): string => {
    const mode = leg.ModeOfTransportation?.toUpperCase() ?? '';
    // eslint-disable-next-line security/detect-object-injection
    return DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS[mode] || 'truck';
};

const findCurrentActiveLeg = (legs: IOrderTrackingLegs[]): IOrderTrackingLegs | undefined => {
    if (legs.length === 0) {
        return undefined;
    }
    const activeOrCompletedLegs = legs.filter(
        leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed
    );
    return activeOrCompletedLegs.length > 0 ? activeOrCompletedLegs[activeOrCompletedLegs.length - 1] : legs[0];
};

const mapOrderTrackingToAnonymousDeliveryOrder = (data: IPostAnonymousDeliveryData, postalCode: string): IAnonymousDeliveryOrder => {
    const legs = data.Legs ?? [];
    const mainCarriageLeg = legs.find(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage) || legs[legs.length - 1];

    const preCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.PreCarriage);
    const mainCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage);
    const onCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.OnCarriage);

    const completedLegsIndices = legs
        .map((leg, index) =>
            leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed ? index : -1
        )
        .filter(index => index !== -1);

    const allLegsCompleted = legs.length > 0 && legs.every(leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed);

    const activeStep = allLegsCompleted
        ? legs.length + 1
        : completedLegsIndices.length > 0
        ? completedLegsIndices[completedLegsIndices.length - 1] + 1
        : 0;

    const ordered = createSyntheticLeg(0, DELIVERY_INFORMATION_LEG_STATES.Ordered, DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.PARCEL);
    const lastEventOverall = allLegsCompleted ? legs[legs.length - 1]?.Events?.slice(-1)[0] : undefined;
    const completed = createSyntheticLeg(
        legs.length + 1,
        'Completed',
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLAG,
        lastEventOverall?.Timestamp
    );

    const legToStep = (leg: IOrderTrackingLegs | ISyntheticLeg): IAnonymousDeliveryStep => {
        const lastEvent = leg.Events?.[leg.Events.length - 1];
        const status: AnonymousDeliveryStepStatus = leg.Id < activeStep ? 'completed' : leg.Id === activeStep ? 'active' : 'upcoming';

        return {
            stepKey: lastEvent?.Name || '',
            icon: legIcon(leg),
            status,
            timestamp: status !== 'upcoming' ? lastEvent?.Timestamp : undefined,
            location: status !== 'upcoming' ? lastEvent?.Location : undefined
        };
    };

    const steps: IAnonymousDeliveryStep[] = [
        ordered,
        findCurrentActiveLeg(preCarriageLegs),
        findCurrentActiveLeg(mainCarriageLegs),
        findCurrentActiveLeg(onCarriageLegs),
        completed
    ]
        .filter((leg): leg is IOrderTrackingLegs | ISyntheticLeg => Boolean(leg))
        .map(legToStep);

    const anonymousEvents: IAnonymousDeliveryEvent[] = legs
        .flatMap(leg => leg.Events ?? [])
        .map(event => ({ stepKey: event.Name, timestamp: event.Timestamp, location: event.Location }))
        .sort((eventA, eventB) => new Date(eventB.timestamp).getTime() - new Date(eventA.timestamp).getTime());

    const mainCarriage: IAnonymousDeliveryMainCarriage = {
        carrierName: mainCarriageLeg?.CarrierName || '',
        containerNumber: mainCarriageLeg?.Container || '',
        containerType: mainCarriageLeg?.ContainerType || '',
        shipNumber: mainCarriageLeg?.ShipFlightNumber || '',
        blNumber: mainCarriageLeg?.BlNumber || ''
    };

    return {
        postalCode,
        deliveryNumber: data.DeliveryNumber || '',
        customerPO: data.CustomerPONumber || '',
        estimatedArrivalDate: data.EstimatedArrivalDate || '',
        steps,
        events: anonymousEvents,
        mainCarriage,
        liveTrackingAvailable: data.LiveTrackingAvailable || false,
        liveTrackingUrl: data.LiveTrackingUrl || ''
    };
};

async function action(input: PostAnonymousDeliveryInput, ctx: Msdyn365.IActionContext): Promise<IPostAnonymousDeliveryResponse> {
    const { trackingId, postalCode } = input;

    if (!trackingId) {
        return {
            success: false,
            message: 'Missing tracking ID.'
        };
    }

    if (!postalCode) {
        return {
            success: false,
            message: 'Please provide your postal code.'
        };
    }

    try {
        const response = await Msdyn365.sendRequest<IPostAnonymousDeliveryData>(
            `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetOrderTracking`,
            'post',
            {
                orderNumber: trackingId,
                verificationCode: postalCode
            },
            {
                headers: {
                    OUN: ctx.requestContext.apiSettings.oun
                }
            }
        );

        if (response.status !== 200 || !response.data || response.data.Message !== 'ORDER_TRACKING_SUCCESS') {
            return {
                success: false,
                message: response.data?.Message
            };
        }

        return {
            success: true,
            order: mapOrderTrackingToAnonymousDeliveryOrder(response.data, postalCode)
        };
    } catch (error) {
        console.error('GetOrderTracking failed.', error);
        return {
            success: false,
            message: 'Validation failed.'
        };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostAnonymousDeliveryResponse>>action,
    id: 'PostAnonymousDelivery',
    input: createInput
});
